import React, { useRef, useCallback, useMemo } from 'react';
import { Document } from '../../../components/Pdf/document';
import { Page, PageRef } from '../../../components/Pdf/page';
import {
  PageAction,
  Zoom,
  Delete,
  RotateRight,
  RotateLeft,
  Divider,
  PdfContainer,
  PageNumber,
  InfoDocument,
} from '../styles';
import { PdfRotation } from 'utils/constants';
import { PageOptions } from '../index';
import utils from 'utils';
import styled from 'styled-components/macro';

// Update StyledDocument to be a regular div since we're not using react-pdf
const StyledDocument = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colorPalette.white};
  justify-content: flex-start;
  padding: 20px;
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.colorPalette.grey.grey5};
  border-radius: 4px;
  margin-bottom: 25px;
`;

// Simplified StyledPageWrapper since opacity is now handled in Page component
const StyledPageWrapper = styled.div`
  margin: 10px;
  margin-bottom: 45px;
  position: relative;
`;

export interface Props {
  thumbnailZoom: number;
  docImage: string;
  zoom?: (page: number) => void;
  fileName?: string;
  setSplitString?: (val: string) => void;
  rotatePdf?: (direction: 'left' | 'rigth', pageNumber: number, currentRotation: PdfRotation) => void;
  pageOptions?: PageOptions[];
  split?: number[];
  toggleDelete?: (pageNumber: number) => void;
  setSplit?: (val: number[]) => void;
  activePdfPageLength?: number;
  setActivePdfPageLength?: (val: number) => void;
}

const PdfCarousel = (props: Props) => {
  const pagesRef = useRef<PageRef[]>([]);
  const {
    docImage,
    thumbnailZoom,
    zoom,
    fileName,
    setSplitString,
    rotatePdf,
    pageOptions,
    split,
    toggleDelete,
    setSplit,
    activePdfPageLength,
    setActivePdfPageLength,
  } = props;
  const t = utils.intl.useTranslator();

  const sortSplit = (split: number[]) => split.sort((a, b) => a - b);

  const getPageStatus = (pageNumber: number) => {
    const index = pageOptions?.findIndex((elem: any) => elem.page === pageNumber);
    if (pageOptions && index !== undefined && index > -1) {
      return pageOptions[index].delete;
    }
    return false;
  };

  const splitToString = (newSplit: number[]) => {
    return newSplit
      .map((elem, index) => {
        if (index === 0 && elem > 1) return `${1}-${elem}`;
        if (index > 0) {
          const previousPageNumber = newSplit[index - 1];
          const currentPageNumber = elem;
          if (currentPageNumber - previousPageNumber === 1) return `${elem}`;
          if (getPageStatus(previousPageNumber + 1)) {
            const excluded = previousPageNumber + 1;
            if (currentPageNumber - excluded === 1) return `${elem}`;
            return `${excluded + 1}-${currentPageNumber}`;
          }
          return `${newSplit[index - 1] + 1}-${elem}`;
        }
        return `${elem}`;
      })
      .join(',');
  };

  const toggleSplit = (index: number) => {
    if (split) {
      const isIndexIncluded = split.includes(index);
      let newSplit: number[] = [];
      if (!isIndexIncluded) {
        newSplit = sortSplit([...split, index]);
      } else {
        newSplit = sortSplit(split.filter((elem: number) => elem !== index));
      }
      setSplit && setSplit(newSplit);
      setSplitString && setSplitString(splitToString(newSplit));
    }
  };

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => setActivePdfPageLength && setActivePdfPageLength(numPages),
    [setActivePdfPageLength],
  );

  const documentOptions = useMemo(
    () => ({
      cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${window.pdfjsLib?.version}/cmaps/`,
      cMapPacked: true,
    }),
    [],
  );

  const pageToArray = (() => {
    if (activePdfPageLength) {
      const arrayOfPages = [];
      for (let i = 1; i <= activePdfPageLength; i++) {
        arrayOfPages.push(i);
      }
      return arrayOfPages;
    }
  })();

  const getRotation = (nPage: number): number => {
    const rotation = pageOptions?.find(({ page }: any) => page === nPage)?.rotation;
    return rotation || 0;
  };

  const rotate = (direction: 'left' | 'rigth', pageNumber: number) => {
    const currentRotation: PdfRotation = getRotation(pageNumber);
    rotatePdf && rotatePdf(direction, pageNumber, currentRotation);
  };

  return docImage ? (
    <PdfContainer>
      <InfoDocument>
        <div>{fileName}</div>
        <div>
          {activePdfPageLength} {t('pages')}
        </div>
      </InfoDocument>
      <Document
        file={`data:application/pdf;base64,${docImage}`}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={console.error}
        options={documentOptions}
      >
        <StyledDocument className="filefixer-container">
          {pageToArray?.map((numberPage, key) => (
            <React.Fragment key={key}>
              <StyledPageWrapper>
                <Page
                  ref={(el: PageRef) => (pagesRef.current[numberPage] = el)}
                  rotate={getRotation(numberPage)}
                  scale={thumbnailZoom}
                  pageNumber={numberPage}
                  exclude={getPageStatus(numberPage)} // Pass exclude prop
                >
                  <PageAction>
                    <Zoom onClick={() => zoom && zoom(numberPage)} data-walkthrough={`zoom-${numberPage}`} />
                    <Delete
                      status={getPageStatus(numberPage)}
                      onClick={toggleDelete ? () => toggleDelete(numberPage) : () => null}
                      data-walkthrough={`delete-${numberPage}`}
                    />
                    <RotateLeft
                      className="filefixer-left"
                      data-walkthrough={`rotate-left-${numberPage}`}
                      onClick={() => rotate('left', numberPage)}
                    />
                    <RotateRight
                      className="filefixer-rigth"
                      data-walkthrough={`rotate-right-${numberPage}`}
                      onClick={() => rotate('rigth', numberPage)}
                    />
                  </PageAction>
                </Page>
                <PageNumber>{numberPage}</PageNumber>
              </StyledPageWrapper>
              <Divider
                key={key + 1}
                data-walkthrough={`split-${key + 1}`}
                active={split?.includes(key + 1)}
                onClick={() => toggleSplit(key + 1)}
              />
            </React.Fragment>
          ))}
        </StyledDocument>
      </Document>
    </PdfContainer>
  ) : null;
};

export default PdfCarousel;
